import { defineStore } from 'pinia';
import { computed, type ComputedRef, reactive, ref, type Ref } from 'vue';
import BillingStep from '../components/CheckoutSteps/BillingStep/BillingStep.vue';
import checkoutApi from '../services/checkoutApi';
import featureCheckoutSchema from '../services/featureCheckout';
import boostCheckoutSchema from '../services/boostCheckout';
import {
  type BillingDetails,
  type Checkouts,
  CheckoutType,
  type OrderItem,
  type PriceMap,
} from '@checkout/types';
import {
  CONTINUE_TO_BILLING_LABEL,
  EXIT_CHECKOUT_LABEL,
  PAYMENT_LABEL,
  PREVIOUS_STEP_LABEL,
} from '~/modules/checkout/constants';
import { useMainStore } from '~/stores/mainStore';

const TAX_RATE = 0.2;

const emptyBillingDetails: BillingDetails[] = [];

const emptyPaymentForm = {
  paymentIntentId: '',
  card: false,
};

export const useCheckoutStore = defineStore('checkout', () => {
  const mainStore = useMainStore();

  const checkouts: Checkouts = {
    [CheckoutType.BOOST]: boostCheckoutSchema,
    [CheckoutType.FEATURE]: featureCheckoutSchema,
  };

  const form = reactive(emptyPaymentForm);

  const loading = ref(false);
  const submit = ref(1);
  const activeStep = ref<number>(0);
  const type = ref<CheckoutType>();
  const items = ref<OrderItem<any>[]>([]);
  const propertyIds = ref<Set<number>>(new Set());
  const returningRoute = ref<string>('/');
  const billingDetails = ref<BillingDetails[]>(emptyBillingDetails);
  const selectedPaymentMethodId = ref<string | null>(null);
  const prices = ref<PriceMap>(new Map());

  const discountCode: Ref<string | null> = ref(null);
  const discountCodeId: Ref<number | null> = ref(null);
  const discountValue: Ref<number | null> = ref(null);
  const fixedBoostsCount = ref<number>(0);

  const checkoutTitle = computed<string>(() => {
    return type.value ? checkouts[type.value].title : '';
  });

  const checkoutComponent = computed<Component | null>(() => {
    if (!type.value) return null;
    return activeStep.value > 0 ? BillingStep : checkouts[type.value].component;
  });

  const backButtonLabel = computed<string>(() =>
    activeStep.value === 0 ? EXIT_CHECKOUT_LABEL : PREVIOUS_STEP_LABEL,
  );
  const continueButtonLabel = computed<string>(() =>
    activeStep.value === 0 ? CONTINUE_TO_BILLING_LABEL : PAYMENT_LABEL,
  );

  const subtotal = computed<number>(() => {
    let total = 0;

    // Add tile boosts (items)
    items.value.forEach((item) => {
      total += item.amount;
    });

    // Add fixed boosts (if enabled)
    if (
      type.value === CheckoutType.BOOST &&
      mainStore.siteSettings.fixedRateBoostEnabled &&
      fixedBoostsCount.value > 0
    ) {
      const priceInPounds = mainStore.siteSettings.fixedBoostPrice ?? 0;
      const priceInPence = priceInPounds * 100;
      total += priceInPence * fixedBoostsCount.value;
    }

    return total;
  });


  /**
   * What we should multiply the subtotal with to get the discounted total
   */
  const discountMultiplier: ComputedRef<number> = computed<number>(
    (): number => {
      return !discountValue.value ? 1 : 1 - discountValue.value / 100;
    },
  );

  /**
   * The final value before tax
   */
  const discountedSubtotal: ComputedRef<number> = computed<number>(() => {
    return subtotal.value * discountMultiplier.value;
  });

  /**
   * The 'positive' difference between original cost and discount cost
   */
  const discountAmount: ComputedRef<number | null> = computed<number | null>(
    (): number | null => {
      return !discountValue.value
        ? null
        : subtotal.value - discountedSubtotal.value;
    },
  );

  const tax = computed<number>(() => {
    return discountedSubtotal.value * TAX_RATE;
  });

  const total = computed<number>(() => {
    return discountedSubtotal.value + tax.value;
  });

  const applyDiscount = (discountData: {
    code: string;
    id: number;
    value: number;
  }): void => {
    discountCode.value = discountData.code;
    discountCodeId.value = discountData.id;
    discountValue.value = discountData.value;
  };

  const clearDiscount = (): void => {
    discountCode.value = null;
    discountCodeId.value = null;
    discountValue.value = null;
  };

  function addItem<MetadataType = any>(item: OrderItem<MetadataType>): void {
    items.value.push(item);
  }

  function removeItem(index: number): void {
    items.value = items.value.splice(index, 1);
  }

  const hasOrderItems = computed<boolean>(() => {
    // If using fixed rate boosts and a count is entered, that's valid
    if (
      type.value === CheckoutType.BOOST &&
      mainStore.siteSettings.fixedRateBoostEnabled &&
      fixedBoostsCount.value > 0
    ) {
      return true;
    }
    // Otherwise, check if there are regular items
    return items.value.length > 0;
  });

  function nextStep(): void {
    if (activeStep.value === 1) {
      submit.value++;
      return;
    }

    activeStep.value = 1;
  }

  async function prevStep(): Promise<void> {
    if (activeStep.value === 0) {
      return await exit();
    }

    activeStep.value = 0;
  }

  async function start<InitData>(
    type: CheckoutType,
    initData?: InitData,
    routeOnComplete?: string,
  ): Promise<void> {
    returningRoute.value = routeOnComplete ?? useRoute().fullPath;
    activeStep.value = 0;

    if (initData) {
      checkouts[type].init(initData);
    }

    await navigateTo(`/checkout?type=${type}`);
  }

  function reset(): void {
    loading.value = false;
    submit.value = 1;
    returningRoute.value = '/';
    items.value = [];
    prices.value = new Map();
    propertyIds.value = new Set();
    billingDetails.value = emptyBillingDetails;
    selectedPaymentMethodId.value = null;
    Object.assign(form, emptyPaymentForm);
  }

  function resetOrderItems(): void {
    items.value = [];
  }

  async function exit(resetCheckout: boolean = true): Promise<void> {
    await navigateTo(returningRoute.value);

    if (resetCheckout) {
      // Reset checkout state
      reset();
    }
    activeStep.value = 0;
  }

  async function complete(): Promise<void> {
    // Clear out any codes for next purchase
    clearDiscount();
    await navigateTo('/checkout/success');
  }

  async function createPaymentIntentClientSecret(): Promise<string | null> {
    const { data } = await checkoutApi.createPaymentIntent({
      amount: total.value,
      description: `${type.value}`,
    });

    if (data.value?.status === 'error') {
      return null;
    }

    return data.value.client_secret;
  }

  async function createCheckoutSession(): Promise<string | null> {
    if (!type.value) {
      return null;
    }

    const payload: {
      type: 'boost' | 'feature';
      quantity?: number;
      fixed_quantity?: number;
      properties?: Array<{ id: string; feature_duration: number }>;
      discount_code?: string | null;
    } = {
      type: type.value,
      discount_code: discountCode.value,
    };

    if (type.value === CheckoutType.BOOST) {
      // Tile boost quantity
      if (items.value.length > 0) {
        const boostItem = items.value[0];
        const tileQty = (boostItem.data as any)?.quantity;

        if (tileQty && tileQty > 0) {
          payload.quantity = tileQty;
        }
      }

      // Fixed boost quantity
      if (mainStore.siteSettings.fixedRateBoostEnabled) {
        const fixedQty = Number(fixedBoostsCount.value);

        if (fixedQty && fixedQty > 0) {
          payload.fixed_quantity = fixedQty; // ✅ SEND SEPARATELY
        }
      }

      // Prevent empty checkout
      if (!payload.quantity && !payload.fixed_quantity) {
        return null;
      }
    } else if (type.value === CheckoutType.FEATURE) {
      // Get properties from items
      payload.properties = items.value
        .filter((item) => item.data && (item.data as any).id)
        .map((item) => {
          const data = item.data as any;
          return {
            id: String(data.id),
            feature_duration: data.featureDuration,
          };
        });
    }

    const { data, error } = await checkoutApi.createCheckoutSession(payload);

    if (error.value || data.value?.status === 'error') {
      return null;
    }

    return data.value?.checkout_url || null;
  }

  const displayItems = computed(() => {
    const list = [...items.value];

    if (
      type.value === CheckoutType.BOOST &&
      mainStore.siteSettings.fixedRateBoostEnabled &&
      fixedBoostsCount.value > 0
    ) {
      const pricePence = (mainStore.siteSettings.fixedBoostPrice ?? 0) * 100;
      const quantity = fixedBoostsCount.value;

      list.push({
        name: `${quantity} Fixed Boosts`,
        amount: pricePence * quantity,
      });
    }

    return list;
  });

  return {
    form,
    // State
    discountAmount,
    discountCode,
    discountedSubtotal,
    loading,
    activeStep,
    backButtonLabel,
    billingDetails,
    selectedPaymentMethodId,
    continueButtonLabel,
    checkouts,
    checkoutComponent,
    checkoutTitle,
    items,
    hasOrderItems,
    propertyIds,
    returningRoute,
    subtotal,
    tax,
    total,
    type,
    submit,
    prices,
    // Methods
    applyDiscount,
    clearDiscount,
    start,
    complete,
    reset,
    exit,
    nextStep,
    prevStep,
    addItem,
    removeItem,
    resetOrderItems,
    createPaymentIntentClientSecret,
    createCheckoutSession,
    fixedBoostsCount,
    displayItems,
  };
});
